import { useEffect, useState } from 'react';
import { fetchGptLiveEnabled } from '../business/voice-agent/voiceMessageNotifications';

/**
 * Whether the current tenant has GPT-Live (an active GPT-Live line, or a host
 * test line). Stays after cutover: the Vapi-native lines (C7) don't get
 * GPT-Live-only UI. Fails closed — false while loading and false if the check
 * errors — so GPT-Live-only UI stays hidden unless the backend says yes.
 *
 * `checking` is true until the first answer arrives, for callers whose layout
 * depends on the answer (so they don't render one layout, then jump to another).
 *
 * @param enabled Skip the request (and return false) until the caller needs the
 *   answer — e.g. while a panel is closed. Re-checks each time it turns true.
 */
export function useGptLiveGate(enabled: boolean = true): { enabled: boolean; checking: boolean } {
  const [gptLiveEnabled, setGptLiveEnabled] = useState(false);
  const [checking, setChecking] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setChecking(true);
    fetchGptLiveEnabled()
      .then((value) => {
        if (!cancelled) setGptLiveEnabled(value === true);
      })
      .catch((error: unknown) => {
        // Gate check, not data: a failure hides the GPT-Live UI rather than breaking the page.
        console.error('Failed to check GPT-Live availability:', error);
        if (!cancelled) setGptLiveEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { enabled: enabled && gptLiveEnabled, checking: enabled && checking };
}

export function useGptLiveEnabled(enabled: boolean = true): boolean {
  return useGptLiveGate(enabled).enabled;
}
